import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { Check, ChevronDownIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

function Accordion({ ...props }: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

function AccordionItem({
  className,
  hideSeparator = false,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item> & { hideSeparator?: boolean }) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn(
        'relative',
        !hideSeparator &&
          'after:absolute after:left-0 after:top-full after:h-px after:w-full after:bg-card last:after:hidden',
        className,
      )}
      {...props}
    />
  );
}

function AccordionTrigger({
  className,
  children,
  fillForm,
  content,
  trailingAction,
  hideChevron,
  chevronColor,
  isEvaluation,
  hasResponse,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  fillForm?: boolean;
  content?: React.ReactNode;
  trailingAction?: React.ReactNode;
  hideChevron?: boolean;
  chevronColor?: string;
  isEvaluation?: boolean;
  hasResponse?: boolean;
}) {
  const showCheckInsteadOfChevron = isEvaluation && hasResponse && !fillForm;

  return (
    <AccordionPrimitive.Header
      className={cn('flex', trailingAction ? 'w-full items-center gap-3' : undefined)}
    >
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'cursor-pointer focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-xl font-medium transition-all outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]_.accordion-chevron]:rotate-180',
          trailingAction && 'min-w-0',
          className,
        )}
        {...props}
      >
        {children}
        <div className="flex items-center gap-2 shrink-0">
          {content != null && content !== '' ? <span className="text-sm">{content}</span> : null}
          {(fillForm || showCheckInsteadOfChevron) && (
            <Check className="text-success size-6 shrink-0 translate-y-0.5" />
          )}
          {!hideChevron && !showCheckInsteadOfChevron && (
            <ChevronDownIcon
              className="accordion-chevron pointer-events-none size-6 shrink-0 translate-y-0.5 transition-transform duration-200"
              style={chevronColor ? { color: chevronColor } : undefined}
            />
          )}
        </div>
      </AccordionPrimitive.Trigger>
      {trailingAction ? (
        <div className="flex shrink-0 items-center gap-2 self-stretch">{trailingAction}</div>
      ) : null}
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm"
      {...props}
    >
      <div className={cn('pt-0 pb-4', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
